<template>
    <el-dialog
        title="消息中心"
        :visible="visible"
        width="68%"
        top="7vh"
        custom-class="message-center-dialog"
        :close-on-click-modal="false"
        @close="handleClose"
    >
        <div class="message-center-content">
            <!-- 顶部筛选与快捷操作 -->
            <div class="header-toolbar">
                <el-radio-group v-model="activeTab" size="small" @change="handleTabChange">
                    <el-radio-button label="all">全部</el-radio-button>
                    <el-radio-button label="unread">
                        未读
                        <span v-if="localUnreadCount > 0" class="tab-badge">({{ localUnreadCount }})</span>
                    </el-radio-button>
                    <el-radio-button label="order_exception">订单异常</el-radio-button>
                    <el-radio-button label="order_completed">订单完成</el-radio-button>
                    <el-radio-button label="shop_offline">店铺掉线</el-radio-button>
                </el-radio-group>

                <div class="header-actions">
                    <el-dropdown size="small" @command="handleSendTest" trigger="click" style="margin-right: 8px;">
                        <el-button
                            size="small"
                            type="warning"
                            plain
                            icon="el-icon-s-promotion"
                            :loading="testSending"
                        >
                            发送测试消息<i class="el-icon-arrow-down el-icon--right"></i>
                        </el-button>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item command="order_exception">
                                <i class="el-icon-warning" style="color: #F56C6C; margin-right: 4px;"></i>
                                测试「订单异常」提醒（红卡片）
                            </el-dropdown-item>
                            <el-dropdown-item command="order_completed">
                                <i class="el-icon-circle-check" style="color: #67C23A; margin-right: 4px;"></i>
                                测试「订单完成」提醒（绿卡片）
                            </el-dropdown-item>
                            <el-dropdown-item command="shop_offline">
                                <i class="el-icon-warning-outline" style="color: #E6A23C; margin-right: 4px;"></i>
                                测试「店铺掉线」提醒（橙卡片）
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>

                    <el-button
                        size="small"
                        icon="el-icon-setting"
                        @click="$emit('open-config')"
                    >订阅设置</el-button>
                    <el-button
                        size="small"
                        icon="el-icon-refresh"
                        :loading="loading"
                        @click="fetchList"
                    >刷新</el-button>
                    <el-button
                        type="primary"
                        size="small"
                        icon="el-icon-check"
                        :disabled="localUnreadCount === 0"
                        @click="handleReadAll"
                    >全部标记已读</el-button>
                </div>
            </div>

            <!-- 消息表格 -->
            <vxe-table
                ref="xTable"
                :data="tableData"
                :loading="loading"
                size="small"
                stripe
                border
                resizable
                auto-resize
                show-overflow
                height="460"
                :row-config="{ keyField: 'id', isHover: true }"
                class="message-table"
                style="width: 100%; margin-top: 14px;"
                header-align="center"
            >
                <!-- 展开行：查看错误堆栈 / 详细内容 -->
                <vxe-table-column type="expand" width="55" align="center">
                    <template #header>
                        <span class="expand-header-label">详情</span>
                    </template>
                    <template #content="{ row }">
                        <div class="expand-detail-container">
                            <div class="detail-row" v-if="row.meta && row.meta.errorCode">
                                <span class="label">错误标识：</span>
                                <span class="value code-tag">{{ row.meta.errorCode }}</span>
                            </div>
                            <div class="detail-row" v-if="row.meta && row.meta.requestId">
                                <span class="label">请求 ID (requestId)：</span>
                                <span class="value">{{ row.meta.requestId }}</span>
                                <el-link type="primary" :underline="false" style="margin-left: 10px;" @click="copyText(row.meta.requestId)">复制ID</el-link>
                            </div>
                            <div class="detail-row" v-if="row.content">
                                <span class="label">详细堆栈 / 说明：</span>
                                <el-link type="primary" :underline="false" style="margin-left: 10px;" @click="copyText(row.content)">复制堆栈信息</el-link>
                                <pre class="detail-content-box">{{ row.content }}</pre>
                            </div>
                            <div class="detail-row" v-else>
                                <span class="label">详细信息：</span>
                                <span>{{ row.summary || '暂无更多详细日志' }}</span>
                            </div>
                        </div>
                    </template>
                </vxe-table-column>

                <!-- 类型/状态 -->
                <vxe-table-column field="type" title="类型" width="110" align="center">
                    <template #default="{ row }">
                        <el-tag
                            size="mini"
                            :type="row.type === 'order_exception' ? 'danger' : (row.type === 'shop_offline' ? 'warning' : 'success')"
                            disable-transitions
                        >
                            <i :class="row.type === 'order_exception' ? 'el-icon-warning' : (row.type === 'shop_offline' ? 'el-icon-warning-outline' : 'el-icon-circle-check')"></i>
                            {{ row.type === 'order_exception' ? '订单异常' : (row.type === 'shop_offline' ? '店铺掉线' : '订单完成') }}
                        </el-tag>
                    </template>
                </vxe-table-column>

                <!-- 消息标题 -->
                <vxe-table-column field="title" title="消息标题" min-width="180" show-overflow>
                    <template #default="{ row }">
                        <span :class="{ 'unread-title': !row.isRead, 'read-title': row.isRead }">
                            <span v-if="!row.isRead" class="unread-dot"></span>
                            {{ row.title }}
                        </span>
                    </template>
                </vxe-table-column>

                <!-- 摘要内容 -->
                <vxe-table-column field="summary" title="摘要说明" min-width="260" show-overflow />

                <!-- 关联订单 -->
                <vxe-table-column title="关联订单" width="160" align="center">
                    <template #default="{ row }">
                        <el-tooltip
                            v-if="row.related && (row.related.orderCode || row.related.orderId)"
                            content="点击复制单号"
                            placement="top"
                            :open-delay="300"
                        >
                            <el-link
                                type="primary"
                                :underline="false"
                                @click="copyOrderCode(row)"
                            >
                                <i class="el-icon-document-copy" style="margin-right: 2px;"></i>
                                {{ row.related.orderCode || row.related.orderId }}
                            </el-link>
                        </el-tooltip>
                        <span v-else class="text-gray">-</span>
                    </template>
                </vxe-table-column>

                <!-- 生成时间 -->
                <vxe-table-column field="createdTime" title="发生时间" width="160" align="center" />

                <!-- 操作 -->
                <vxe-table-column title="操作" width="150" align="center" fixed="right" :show-overflow="false">
                    <template #default="{ row }">
                        <el-button
                            v-if="!row.isRead"
                            type="text"
                            size="mini"
                            icon="el-icon-check"
                            @click="handleMarkRead(row)"
                        >标为已读</el-button>
                    </template>
                </vxe-table-column>

                <!-- 空状态自定义插槽 -->
                <template #empty>
                    <div class="empty-state-wrapper">
                        <div class="empty-icon-circle">
                            <i class="el-icon-bell"></i>
                        </div>
                        <div class="empty-title">
                            {{ activeTab === 'unread' ? '暂无未读消息' : '暂无消息记录' }}
                        </div>
                        <div class="empty-subtitle">
                            系统默认开启源头防打扰，当订单出现欠费、中断或完成交付时将在此展示与提醒
                        </div>
                        <div class="empty-actions">
                            <el-button
                                size="mini"
                                type="primary"
                                plain
                                icon="el-icon-setting"
                                @click="$emit('open-config')"
                            >
                                配置消息订阅偏好
                            </el-button>
                        </div>
                    </div>
                </template>
            </vxe-table>

            <!-- 分页 -->
            <vxe-pager
                perfect
                size="small"
                align="right"
                class="message-center-pager"
                :current-page.sync="page"
                :page-size.sync="pageSize"
                :total="total"
                :page-sizes="[10, 20, 50]"
                @page-change="handlePageChange"
                :layouts="['PrevPage', 'Number', 'NextPage', 'Sizes', 'FullJump', 'Total']"
            />
        </div>
    </el-dialog>
</template>

<script>
export default {
    name: "MessageCenterDialog",
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        unreadCount: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            loading: false,
            testSending: false,
            activeTab: 'all', // 'all' | 'unread' | 'order_exception' | 'order_completed' | 'shop_offline'
            tableData: [],
            page: 1,
            pageSize: 10,
            total: 0,
            localUnreadCount: this.unreadCount
        };
    },
    watch: {
        visible(val) {
            if (val) {
                this.page = 1;
                this.fetchList();
                this.fetchUnreadCount();
                this.$nextTick(() => {
                    if (this.$refs.xTable) {
                        this.$refs.xTable.recalculate();
                    }
                });
            }
        },
        unreadCount(val) {
            this.localUnreadCount = val;
            if (this.visible) {
                this.fetchList();
            }
        }
    },
    methods: {
        async handleSendTest(type) {
            this.testSending = true;
            try {
                const res = await this.$http.post('/messageCenter/test', {
                    type,
                    ignoreSubscription: true
                });
                if (res && res.success) {
                    this.$message.success('测试消息已触发，请观察前台卡片与桌面通知！');
                    this.fetchList();
                    this.fetchUnreadCount();
                }
            } catch (e) {
                // 异常已由 axios 响应拦截器统一弹出提示，此处无需重复报错
            } finally {
                this.testSending = false;
            }
        },
        handleClose() {
            this.$emit('update:visible', false);
        },
        handleTabChange() {
            this.page = 1;
            this.fetchList();
        },
        handlePageChange(params) {
            if (typeof params === 'number') {
                this.page = params;
            } else if (params && typeof params === 'object') {
                this.page = params.currentPage || this.page;
                this.pageSize = params.pageSize || this.pageSize;
            }
            this.fetchList();
        },
        async fetchUnreadCount() {
            try {
                const res = await this.$http.get('/messageCenter/unreadCount');
                if (res && res.success) {
                    this.localUnreadCount = res.data?.unreadCount || 0;
                    this.$emit('change-unread', this.localUnreadCount);
                }
            } catch (e) {
                // ignore
            }
        },
        async fetchList() {
            this.loading = true;
            try {
                const params = {
                    page: this.page,
                    pageSize: this.pageSize
                };

                if (this.activeTab === 'unread') {
                    params.isRead = false;
                } else if (this.activeTab !== 'all') {
                    params.type = this.activeTab;
                }

                const res = await this.$http.get('/messageCenter/page', { params });
                const pageResult = res?.data || res || {};
                const list = Array.isArray(pageResult.list) ? pageResult.list : (Array.isArray(res?.data?.list) ? res.data.list : []);
                this.tableData = list;
                this.total = typeof pageResult.total === 'number' ? pageResult.total : (this.tableData.length || 0);
                this.$nextTick(() => {
                    if (this.$refs.xTable) {
                        this.$refs.xTable.recalculate();
                    }
                });
            } catch (e) {
                // 异常已由 axios 统一拦截处理
            } finally {
                this.loading = false;
            }
        },
        async handleMarkRead(row) {
            try {
                const res = await this.$http.post(`/messageCenter/${row.id}/read`);
                if (res && res.success) {
                    this.$message.success('已标记已读');
                    row.isRead = true;
                    if (res.data && typeof res.data.unreadCount === 'number') {
                        this.localUnreadCount = res.data.unreadCount;
                        this.$emit('change-unread', this.localUnreadCount);
                    }
                    if (this.activeTab === 'unread') {
                        this.fetchList();
                    }
                }
            } catch (e) {
                // 异常已由 axios 统一拦截处理
            }
        },
        async handleReadAll() {
            try {
                await this.$confirm('确定将所有未读消息标记为已读吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'info'
                });

                const payload = {};
                if (this.activeTab !== 'all' && this.activeTab !== 'unread') {
                    payload.type = this.activeTab;
                }

                const res = await this.$http.post('/messageCenter/readAll', payload);
                if (res && res.success) {
                    this.$message.success('已全部标记为已读');
                    if (res.data && typeof res.data.unreadCount === 'number') {
                        this.localUnreadCount = res.data.unreadCount;
                        this.$emit('change-unread', this.localUnreadCount);
                    }
                    this.fetchList();
                }
            } catch (e) {
                // 包含确认框取消或请求异常（异常已由 axios 拦截器统一提示）
            }
        },
        handleJumpOrder(row) {
            if (!row.related || !row.related.orderId) return;
            const targetOrderId = row.related.orderId;
            const targetBusCode = row.related.orderCode;
            const routerPath = row.related.routerPath || '/order/order';

            // 标记已读
            if (!row.isRead) {
                this.handleMarkRead(row);
            }

            // 关闭弹窗并跳转
            this.handleClose();
            const currentQuery = this.$route.query;
            if (this.$route.path !== routerPath || currentQuery.orderId !== targetOrderId || currentQuery.busCode !== targetBusCode) {
                this.$router.push({
                    path: routerPath,
                    query: { orderId: targetOrderId, busCode: targetBusCode }
                });
            }
        },
        copyOrderCode(row) {
            const code = (row.related && (row.related.orderCode || row.related.orderId)) || '';
            if (!code) {
                this.$message.warning('暂无可用单号');
                return;
            }
            this.copyText(code, '已复制单号到剪贴板');
        },
        copyText(text, successMsg = '已复制到剪贴板') {
            if (!text) return;
            if (this.$copyText) {
                this.$copyText(String(text)).then(() => {
                    this.$message.success(successMsg);
                }).catch(() => {
                    this._fallbackCopyText(text, successMsg);
                });
            } else if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(String(text)).then(() => {
                    this.$message.success(successMsg);
                }).catch(() => {
                    this._fallbackCopyText(text, successMsg);
                });
            } else {
                this._fallbackCopyText(text, successMsg);
            }
        },
        _fallbackCopyText(text, successMsg = '已复制到剪贴板') {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = String(text);
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (successful) {
                    this.$message.success(successMsg);
                } else {
                    this.$message.error('复制失败');
                }
            } catch (e) {
                this.$message.error('复制失败');
            }
        }
    }
};
</script>

<style scoped lang="scss">
.message-center-content {
    display: flex;
    flex-direction: column;
}

.header-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .tab-badge {
        color: #f56c6c;
        font-weight: bold;
        margin-left: 2px;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}

.expand-header-label {
    color: #909399;
    font-size: 12px;
}

.unread-title {
    font-weight: 600;
    color: #303133;
}

.read-title {
    color: #606266;
}

.unread-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #f56c6c;
    margin-right: 6px;
    vertical-align: middle;
}

.text-gray {
    color: #909399;
}

.expand-detail-container {
    padding: 12px 20px;
    background-color: #f8f9fa;
    border-radius: 4px;
    text-align: left;

    .detail-row {
        margin-bottom: 8px;
        line-height: 1.6;

        &:last-child {
            margin-bottom: 0;
        }

        .label {
            font-weight: 600;
            color: #606266;
            margin-right: 8px;
        }

        .code-tag {
            background-color: #fef0f0;
            color: #f56c6c;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 12px;
            font-family: monospace;
        }

        .detail-content-box {
            margin-top: 6px;
            padding: 8px 12px;
            background-color: #282c34;
            color: #abb2bf;
            border-radius: 4px;
            font-size: 12px;
            max-height: 180px;
            overflow: auto;
            white-space: pre-wrap;
            word-break: break-all;
        }
    }
}

.empty-state-wrapper {
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .empty-icon-circle {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background-color: #f2f6fc;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 12px;

        i {
            font-size: 26px;
            color: #909399;
        }
    }

    .empty-title {
        font-size: 14px;
        color: #606266;
        font-weight: 600;
        margin-bottom: 6px;
    }

    .empty-subtitle {
        font-size: 12px;
        color: #909399;
        max-width: 420px;
        line-height: 1.6;
        margin-bottom: 16px;
        text-align: center;
    }

    .empty-actions {
        display: flex;
        justify-content: center;
    }
}

.message-center-pager {
    margin-top: 12px;
}
</style>
